import React, { useRef, useEffect } from 'react';

import {
    Button,
    Box,
    TextField,
    Typography,
} from "@mui/material";

import {
    DatePicker,
    LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { validateClassDateRange } from './helpers';

function normalize(s) {
    if (!s) return '';
    return s.trim().toLowerCase();
}

const CreateClassForm = ({
    editMode,
    classes,
    userInput,
    setUserInput,
    setActiveStep,
    nameError,
    setNameError,
    // Only the edit dialog has date fields; the create wizard passes no
    // date-error state, and the no-op defaults keep validate() uniform.
    startDateError,
    setStartDateError = () => {},
    endDateError,
    setEndDateError = () => {},
    onDelete,
    onSave,
    onCancel,
    errorMessage,
}) => {
    const inputRef = useRef();

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.select();
        }
    }, []);

    const validate = () => {
        const duplicate = classes.some(schoolClass =>
            normalize(schoolClass.name) === normalize(userInput.name) &&
            normalize(schoolClass.level) === normalize(userInput.level) &&
            normalize(schoolClass.term) === normalize(userInput.term)
        );
        const dateErrors = validateClassDateRange(userInput.start_date, userInput.end_date);
        setStartDateError(dateErrors.start_date);
        setEndDateError(dateErrors.end_date);
        if (!userInput.name) {
            setNameError("Class name is required");
            return false;
        }
        if (duplicate) {
            setNameError("Class name is taken");
            return false;
        }
        return !dateErrors.start_date && !dateErrors.end_date;
    };

    const handleNext = () => {
        if (validate()) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleDelete = () => {
        onDelete();
    };

    const handleSave = () => {
        if (validate()) {
            onSave();
        }
    };

    const handleCancel = () => {
        onCancel();
    };

    const handleInputChange = (e) => {
        setNameError(false);
        setUserInput({
            ...userInput,
            [e.target.name]: e.target.value,
        });
    };

    const handleDateChange = (name) => (value) => {
        setStartDateError(false);
        setEndDateError(false);
        setUserInput({
            ...userInput,
            [name]: value,
        });
    };

    return (
        <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h5">{editMode ? 'Edit Class' : 'Create Class'}</Typography>
            <Typography variant="body1" sx={{ mb: 3, pl: '100px', pr: '100px' }}>
                Give the class or group of students a name and level if applicable. The names of your classes will be listed together.
            </Typography>
            <Box sx={{ display: 'block', marginBottom: '16px' }}>
                <TextField
                    inputRef={inputRef}
                    autoFocus
                    required
                    margin="dense"
                    name="name"
                    label="Name of group or class"
                    fullWidth
                    value={userInput.name}
                    onChange={handleInputChange}
                    error={nameError !== false}
                    helperText={nameError}
                    sx={{ mt: 3, mb: 1, width: '250px' }}
                    autoComplete="off"
                />
            </Box>
            <Box sx={{ display: 'block' }}>
                <TextField
                    margin="dense"
                    name="level"
                    label="Level"
                    fullWidth
                    value={userInput.level}
                    onChange={handleInputChange}
                    sx={{ mb: 3, width: '250px' }}
                    autoComplete="off"
                />
            </Box>
            <Box sx={{ display: 'block' }}>
                <TextField
                    margin="dense"
                    name="term"
                    label="Academic term"
                    fullWidth
                    value={userInput.term}
                    onChange={handleInputChange}
                    sx={{ mb: 3, width: '250px' }}
                    autoComplete="off"
                />
            </Box>
            {editMode && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: '60px', pr: '60px' }}>
                        The class is active from the first date through the last, inclusive;
                        leave a date empty to keep that side open. While active, the class
                        counts towards the school’s student licenses and gives its students
                        access to the app, and the study times reported for the students
                        cover only this period.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                        <DatePicker
                            label="Active from"
                            value={userInput.start_date || null}
                            onChange={handleDateChange('start_date')}
                            maxDate={userInput.end_date?.isValid() ? userInput.end_date : undefined}
                            slotProps={{
                                textField: { margin: 'dense', error: startDateError !== false, helperText: startDateError || undefined, sx: { width: '250px' } },
                                actionBar: { actions: ['clear', 'accept'] },
                            }}
                        />
                        <DatePicker
                            label="Active until"
                            value={userInput.end_date || null}
                            onChange={handleDateChange('end_date')}
                            minDate={userInput.start_date?.isValid() ? userInput.start_date : undefined}
                            slotProps={{
                                textField: { margin: 'dense', error: endDateError !== false, helperText: endDateError || undefined, sx: { width: '250px' } },
                                actionBar: { actions: ['clear', 'accept'] },
                            }}
                        />
                    </Box>
                </LocalizationProvider>
            )}
            {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
            { editMode ?
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, mb: 2 }}>
                    <Box sx={{ mr: 5 }}>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                        >
                            Delete
                        </Button>
                    </Box>
                    <Box>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ mr: 2 }}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{ mr: 2 }}
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            :
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2, mb: 2 }}
                    onClick={handleNext}
                >
                    Continue
                </Button>
            }
        </Box>
    );
};

export default CreateClassForm;
